import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { SaveResult, Script, ScriptStatus } from '../../../shared/types';
import { currentRound, roundForStatus } from '../../../shared/publish';
import { adminApi } from '../../lib/api';
import { publishApi, type PublishInfo } from '../../lib/publish';
import ConfirmModal from '../ConfirmModal';
import { showToast } from '../Toaster';
import { STATUS_PILL_LABELS, type StatusPillLabel } from '../EditorTopBar/statusLabels';
import type { PublishZone } from '../EditorTopBar/DocState';
import PublishDialog, { type PublishOutcome } from './PublishDialog';
import { remindItem } from './remindCopy';

const JUST_PUBLISHED_MS = 6000;

function infoFrom(script: Script): PublishInfo {
  return {
    published_round: script.published_round ?? null,
    published_at: script.published_at ?? null,
    published_version_id: script.published_version_id ?? null,
    published_doc_version: script.published_doc_version ?? null,
    published_title: script.published_title ?? null,
    published_note: script.published_note ?? '',
    round_published_at: script.round_published_at ?? null,
    last_client_feedback_at: script.last_client_feedback_at ?? null,
    last_client_feedback_by: script.last_client_feedback_by ?? '',
    last_reminded_at: script.last_reminded_at ?? null,
    approved_at: script.approved_at ?? null,
    approved_by_name: script.approved_by_name ?? null,
    email_enabled: script.email_enabled ?? false,
    unpublished: script.unpublished ?? 'client',
  };
}

/**
 * Everything the editor needs for publish revisions (2A), kept out of
 * AdminEditScript so that page only threads props through: the zone 2 state,
 * the Publish button and dialog, the status pill's new rules, Remind client
 * and Take the page down.
 */
export function usePublishControls(args: {
  script: Script | null;
  status: ScriptStatus;
  setStatus: (s: ScriptStatus) => void;
  version: number;
  flush: () => Promise<boolean>;
  onOpenAdminSettings: () => void;
  onReviewSuggestion: () => void;
}) {
  const { script, status, setStatus, version, flush } = args;
  const [info, setInfo] = useState<PublishInfo | null>(null);
  const [dialog, setDialog] = useState<{ preferRound: boolean; targetRound?: number } | null>(null);
  const [justPublished, setJustPublished] = useState<PublishZone['justPublished']>(null);
  const [confirm, setConfirm] = useState<'remind' | 'takedown' | 'approve' | null>(null);
  const versionRef = useRef(version);
  versionRef.current = version;
  const scriptId = script?.id ?? '';

  useEffect(() => {
    if (script) setInfo(infoFrom(script));
    // Seed once per loaded script; later changes come from saves and publishes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [script?.id]);

  useEffect(() => {
    if (!justPublished) return;
    const t = window.setTimeout(() => setJustPublished(null), JUST_PUBLISHED_MS);
    return () => window.clearTimeout(t);
  }, [justPublished]);

  const refresh = useCallback(async () => {
    if (!scriptId) return;
    const fresh = await adminApi.getScript(scriptId);
    setInfo(infoFrom(fresh));
    setStatus(fresh.status);
  }, [scriptId, setStatus]);

  const onSaved = useCallback((result: SaveResult) => {
    if (result.unpublished) setInfo((prev) => (prev ? { ...prev, unpublished: result.unpublished! } : prev));
  }, []);

  const openPublish = useCallback(async (preferRound = false, targetRound?: number) => {
    if (!(await flush())) {
      showToast('error', 'Retry the save first, so the client gets what you see.');
      return;
    }
    setDialog({ preferRound, targetRound });
  }, [flush]);

  const everPublished = (info?.published_round ?? null) !== null;
  const round = info ? currentRound(status, info.published_round) ?? info.published_round : null;

  /** True when the pill choice is handled here instead of a plain status change. */
  const interceptStatusChange = useCallback(
    async (next: ScriptStatus): Promise<boolean> => {
      if (!(await flush())) {
        showToast('error', 'Save the pending document changes before changing status.');
        return true;
      }
      if (!info) return false;
      const nextRound = roundForStatus(next);
      const fromRound = status === 'draft' ? 0 : roundForStatus(status) ?? 0;
      // Choosing a later round is publishing it: one act, through the dialog.
      if (next !== 'approved' && nextRound !== null && (status === 'draft' || nextRound > fromRound)) {
        await openPublish(nextRound !== info.published_round, nextRound);
        return true;
      }
      if (next === 'approved') {
        // Read after flush, not from the render that started the save.
        const fresh = await adminApi.getScript(scriptId);
        const latest = infoFrom(fresh);
        setInfo(latest);
        if (!latest.published_version_id) {
          showToast('error', 'Publish it first so the client has a version to approve.');
          return true;
        }
        if (latest.unpublished === 'client') {
          setConfirm('approve');
          return true;
        }
      }
      return false;
    },
    [info, status, openPublish, scriptId, flush],
  );

  async function onPublished(outcome: PublishOutcome) {
    setDialog(null);
    setStatus(outcome.result.status);
    setJustPublished({ round: outcome.result.published_round, quiet: outcome.quiet, emailed: outcome.emailed });
    if (outcome.withoutSummary) showToast('success', 'Sent without a summary.');
    await refresh().catch(() => {});
  }

  async function sendReminder() {
    const res = await publishApi.remind(scriptId);
    setInfo((prev) => (prev ? { ...prev, last_reminded_at: res.last_reminded_at } : prev));
    setConfirm(null);
    showToast('success', 'Reminder sent to the client.');
  }

  async function takeDown() {
    const res = await adminApi.setStatus(scriptId, 'draft');
    setStatus(res.status);
    setConfirm(null);
    showToast('success', 'The client page is down. Publish to bring it back.');
  }

  async function approveAsSent() {
    const res = await adminApi.setStatus(scriptId, 'approved');
    setStatus(res.status);
    setConfirm(null);
    await refresh().catch(() => {});
  }

  const statusLabels = useCallback(
    (s: ScriptStatus): StatusPillLabel =>
      s === 'v3' && round !== null && round > 3
        ? { full: `Round ${round}`, short: `R${round}`, phone: `R${round}` }
        : STATUS_PILL_LABELS[s],
    [round],
  );

  const remind = info && script
    ? remindItem({ status, archivedAt: script.archived_at, clientEmail: script.client_email, info })
    : null;

  const topBarProps = info
    ? {
        publishZone: {
          status,
          kind: info.unpublished,
          round: everPublished ? round : null,
          publishedAt: info.published_at,
          justPublished,
          clientName: script?.client_name ?? '',
        } satisfies PublishZone,
        showPublish: status === 'draft' || info.unpublished !== 'none' || !everPublished,
        onPublish: () => void openPublish(false),
        statusLabels,
        // After the first publish Draft leaves the menu: there is nothing to
        // hide any more, and taking the page down lives in More.
        statusOptions: everPublished && status !== 'draft'
          ? (['v1', 'v2', 'v3', 'approved'] as ScriptStatus[])
          : undefined,
        remind: remind ? { enabled: remind.enabled, line: remind.line, onSelect: () => setConfirm('remind') } : undefined,
        onTakeDown: everPublished && status !== 'draft' ? () => setConfirm('takedown') : null,
      }
    : {};

  let modals: ReactNode = null;
  if (dialog && script && info) {
    modals = (
      <PublishDialog
        scriptId={script.id}
        title={script.title}
        clientName={script.client_name}
        status={status}
        info={info}
        expectedVersion={() => versionRef.current}
        flush={flush}
        preferRound={dialog.preferRound}
        targetRound={dialog.targetRound}
        onClose={() => setDialog(null)}
        onPublished={(o) => void onPublished(o)}
        onOpenAdminSettings={() => { setDialog(null); args.onOpenAdminSettings(); }}
        onReviewSuggestion={() => { setDialog(null); args.onReviewSuggestion(); }}
      />
    );
  } else if (confirm === 'remind' && remind) {
    modals = (
      <ConfirmModal
        eyebrow="Remind client"
        title={`Remind the client about v${remind.round}?`}
        body={remind.confirmBody}
        confirmLabel="Send reminder"
        busyLabel="Sending…"
        onCancel={() => setConfirm(null)}
        onConfirm={sendReminder}
      />
    );
  } else if (confirm === 'takedown') {
    modals = (
      <ConfirmModal
        eyebrow="Client link"
        title="Take the client page down?"
        body="Everyone with the link sees “This script is being revised” until you publish again. Comments and the approve button go with it. Use this only for genuine emergencies."
        confirmLabel="Take the page down"
        busyLabel="Taking it down…"
        onCancel={() => setConfirm(null)}
        onConfirm={takeDown}
      />
    );
  } else if (confirm === 'approve' && round !== null) {
    modals = (
      <ConfirmModal
        eyebrow="Approve"
        title="You have unpublished changes"
        body={`Publish your changes before approving, or approve v${round} as the client sees it? Approving now keeps your changes in the working copy, unpublished. To include them, cancel and publish first.`}
        confirmLabel={`Approve v${round} as sent`}
        busyLabel="Approving…"
        onCancel={() => setConfirm(null)}
        onConfirm={approveAsSent}
      />
    );
  }

  return { info, topBarProps, interceptStatusChange, onSaved, modals, openPublish, refresh };
}
